import { mergePublicSearchPages } from "./search-pagination";
import type {
  PublicGiftListItem,
  PublicSearchMetadata,
  PublicSearchPage,
  SearchCriteria,
} from "./public-search-types";

export type FailedOperation =
  | { kind: "initial"; criteria: SearchCriteria; page: 1 }
  | { kind: "load-more"; criteria: SearchCriteria; page: number };

type PreviewState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "unavailable" }
  | { status: "ready"; page: PublicSearchPage };

interface SearchResultsData {
  criteria: SearchCriteria;
  items: PublicGiftListItem[];
  metadata: PublicSearchMetadata;
}

export type PublicSearchState =
  | { phase: "idle" }
  | { phase: "loading"; failedOperation: Extract<FailedOperation, { kind: "initial" }> }
  | (SearchResultsData & {
      phase: "results";
      failedOperation: Extract<FailedOperation, { kind: "load-more" }> | null;
    })
  | (SearchResultsData & { phase: "load-more"; pendingPage: number })
  | {
      phase: "empty";
      criteria: SearchCriteria;
      metadata: PublicSearchMetadata;
      preview: PreviewState;
    }
  | { phase: "error"; failedOperation: Extract<FailedOperation, { kind: "initial" }> };

export type PublicSearchAction =
  | { type: "SUBMIT"; criteria: SearchCriteria }
  | { type: "INITIAL_SUCCEEDED"; page: PublicSearchPage }
  | { type: "INITIAL_FAILED" }
  | { type: "LOAD_MORE_STARTED"; page: number }
  | { type: "LOAD_MORE_SUCCEEDED"; page: PublicSearchPage }
  | { type: "LOAD_MORE_FAILED" }
  | { type: "RETRY" }
  | { type: "PREVIEW_STARTED" }
  | { type: "PREVIEW_SUCCEEDED"; page: PublicSearchPage }
  | { type: "PREVIEW_FAILED" }
  | { type: "PREVIEW_ADOPTED" }
  | { type: "CLEAR" };

export const initialPublicSearchState: PublicSearchState = { phase: "idle" };

function toMetadata(page: PublicSearchPage): PublicSearchMetadata {
  return {
    page: page.page,
    pageSize: page.pageSize,
    totalItems: page.totalItems,
    totalPages: page.totalPages,
    hasNextPage: page.hasNextPage,
  };
}

function isCombinedCriteria(criteria: SearchCriteria): criteria is Required<SearchCriteria> {
  return criteria.query !== undefined && criteria.eventDateFrom !== undefined;
}

/** Eligible only for an empty combined search — otherwise there is no filter to relax. */
export function canStartPreview(state: PublicSearchState): boolean {
  return (
    state.phase === "empty" &&
    state.preview.status === "idle" &&
    isCombinedCriteria(state.criteria)
  );
}

export function publicSearchReducer(
  state: PublicSearchState,
  action: PublicSearchAction,
): PublicSearchState {
  switch (action.type) {
    case "SUBMIT":
      return {
        phase: "loading",
        failedOperation: { kind: "initial", criteria: action.criteria, page: 1 },
      };

    case "INITIAL_SUCCEEDED": {
      if (state.phase !== "loading") return state;
      const criteria = state.failedOperation.criteria;
      const metadata = toMetadata(action.page);

      if (action.page.items.length === 0) {
        return { phase: "empty", criteria, metadata, preview: { status: "idle" } };
      }

      return {
        phase: "results",
        criteria,
        items: mergePublicSearchPages([], action.page.items),
        metadata,
        failedOperation: null,
      };
    }

    case "INITIAL_FAILED":
      return state.phase === "loading"
        ? { phase: "error", failedOperation: state.failedOperation }
        : state;

    case "LOAD_MORE_STARTED":
      if (
        state.phase !== "results" ||
        !state.metadata.hasNextPage ||
        action.page !== state.metadata.page + 1
      ) {
        return state;
      }
      return {
        phase: "load-more",
        criteria: state.criteria,
        items: state.items,
        metadata: state.metadata,
        pendingPage: action.page,
      };

    case "LOAD_MORE_SUCCEEDED":
      if (
        state.phase !== "load-more" ||
        action.page.page !== state.pendingPage
      ) {
        return state;
      }
      return {
        phase: "results",
        criteria: state.criteria,
        items: mergePublicSearchPages(state.items, action.page.items),
        metadata: toMetadata(action.page),
        failedOperation: null,
      };

    case "LOAD_MORE_FAILED":
      if (state.phase !== "load-more") return state;
      return {
        phase: "results",
        criteria: state.criteria,
        items: state.items,
        metadata: state.metadata,
        failedOperation: {
          kind: "load-more",
          criteria: state.criteria,
          page: state.pendingPage,
        },
      };

    case "RETRY":
      if (state.phase === "error") {
        return { phase: "loading", failedOperation: state.failedOperation };
      }
      if (state.phase === "results" && state.failedOperation !== null) {
        return {
          phase: "load-more",
          criteria: state.criteria,
          items: state.items,
          metadata: state.metadata,
          pendingPage: state.failedOperation.page,
        };
      }
      return state;

    case "PREVIEW_STARTED":
      if (!canStartPreview(state) || state.phase !== "empty") {
        return state;
      }
      return { ...state, preview: { status: "loading" } };

    case "PREVIEW_SUCCEEDED":
      if (state.phase !== "empty" || state.preview.status !== "loading") return state;
      return {
        ...state,
        preview:
          action.page.items.length === 0
            ? { status: "unavailable" }
            : { status: "ready", page: action.page },
      };

    case "PREVIEW_FAILED":
      return state.phase === "empty" && state.preview.status === "loading"
        ? { ...state, preview: { status: "unavailable" } }
        : state;

    case "PREVIEW_ADOPTED":
      if (
        state.phase !== "empty" ||
        state.preview.status !== "ready" ||
        state.criteria.query === undefined
      ) {
        return state;
      }
      return {
        phase: "results",
        criteria: { query: state.criteria.query },
        items: mergePublicSearchPages([], state.preview.page.items),
        metadata: toMetadata(state.preview.page),
        failedOperation: null,
      };

    case "CLEAR":
      return initialPublicSearchState;
  }
}
